import { appRoutes } from 'config';
import { isProtectedPath } from 'config/routing';
import { isBrowser } from 'utils/BrowserUtils';
import { usePathname } from 'core/i18n/navigation';
import { useRouter } from 'next/navigation';
import { useEffect, type ReactElement } from 'react';
import { useAuthentication } from 'xstate/hooks/useAuthentication';

interface Props {
  children: ReactElement;
}

const RouteProtector = ({ children }: Props): ReactElement => {
  const { isUnauthenticated } = useAuthentication();
  // next-intl usePathname is locale-agnostic and resolved (e.g. `/blog/my-post`,
  // `/spaces/123`), which is exactly what isProtectedPath expects.
  const pathname = usePathname();
  const router = useRouter();

  const isProtected = isProtectedPath(pathname);

  useEffect(() => {
    // Any page that isn't public needs a signed-in user. Send visitors to
    // the login page, but only once we know for sure they are signed out.
    // On a fresh page load the auth machine starts by waiting for Firebase to
    // say whether there is a session; redirecting during that wait would
    // bounce a signed-in user to the login page and straight back.
    if (isProtected && isBrowser() && isUnauthenticated) {
      router.push(appRoutes.LOGIN);
    }
  }, [isProtected, isUnauthenticated, router]);

  return children;
};

export default RouteProtector;
